import React from "react";
import { Link } from "react-router-dom";
import { Heart, Star, MapPin, Check } from "lucide-react";
import MatchBadge from "./MatchBadge";
import FilterChip from "./FilterChip";
import Button from "./Button";
import { formatRupiahShort } from "../lib/format";
import { useCompare } from "../context/CompareContext";
import { useLikes } from "../context/LikesContext";

// Reusable school card used on Landing, Explore and Match results.
export default function SchoolCard({ school, matchScore, className = "" }) {
  const { isInCompare, toggleCompare } = useCompare();
  const { isLiked, toggleLike } = useLikes();
  const score = matchScore ?? school.matchScore;
  const inCompare = isInCompare(school.slug);
  const fav = isLiked(school.slug);

  return (
    <article
      data-testid={`school-card-${school.slug}`}
      className={`group bg-warm ink-border rounded-card overflow-hidden flex flex-col transition-transform duration-200 hover:-translate-y-1.5 hover:shadow-offset-sm ${className}`}
    >
      <div className="relative">
        <Link to={`/schools/${school.slug}`} className="block">
          <div className="aspect-[16/10] overflow-hidden bg-cream-2">
            <img
              src={school.image}
              alt={school.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>

        {score != null && (
          <div className="absolute top-3 left-3">
            <MatchBadge score={score} size="sm" />
          </div>
        )}

        <button
          type="button"
          onClick={() => toggleLike(school.slug)}
          aria-label={fav ? `Hapus ${school.name} dari sekolah disukai` : `Sukai ${school.name}`}
          data-testid={`favorite-${school.slug}`}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-warm ink-border flex items-center justify-center hover:bg-cream-2 transition-colors"
        >
          <Heart
            size={18}
            className={fav ? "fill-coral text-coral" : "text-ink"}
          />
        </button>
      </div>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div>
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <Link to={`/schools/${school.slug}`}>
              <h3 className="text-xl font-extrabold leading-tight hover:text-purple transition-colors">
                {school.name}
              </h3>
            </Link>
            <span className="inline-flex items-center gap-1 text-sm font-bold shrink-0 bg-yellow/20 px-2 py-0.5 rounded-lg text-ink">
              <Star size={14} className="fill-yellow text-yellow" />
              {school.rating}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-sm font-medium text-ink/70">
            <MapPin size={15} />
            {school.city} • {school.distanceKm} km
          </div>
        </div>

        <div className="flex items-end gap-2 mt-1">
          <div className="text-xl font-extrabold text-ink">{formatRupiahShort(school.monthlyTuition)}</div>
          <div className="text-xs font-semibold text-ink/50 mb-1">/ bulan</div>
        </div>

        <span className="text-sm font-bold text-purple bg-purple/10 w-fit px-2.5 py-0.5 rounded-md">{school.curriculum}</span>

        <div className="flex flex-wrap gap-1.5 mt-auto">
          {school.tags.slice(0, 3).map((t) => (
            <FilterChip as="span" key={t} className="!px-2.5 !py-1 !text-xs !bg-cream-2 !border-transparent">
              {t}
            </FilterChip>
          ))}
        </div>

        <div className="pt-3 flex gap-2 w-full">
          <Button
            variant={inCompare ? "mint" : "secondary"}
            size="sm"
            className="flex-1"
            onClick={() => toggleCompare(school.slug)}
            data-testid={`compare-toggle-${school.slug}`}
          >
            {inCompare ? <><Check size={16} className="inline mr-1" /> Dibandingkan</> : "Bandingkan"}
          </Button>
          <Button to={`/schools/${school.slug}`} size="sm" className="flex-1" data-testid={`view-school-${school.slug}`}>
            Lihat →
          </Button>
        </div>
      </div>
    </article>
  );
}
